// src/App.tsx — role-aware shell + router.
//
// Role detection (first render after auth): the auth exchange already returns
// isAdmin (derived server-side from the participant row's is_admin flag OR the
// pulse.admin scope). We confirm it against /api/pulse/me so the rendered route
// set always matches the authoritative participant record.
import { Route, Switch, Redirect } from "wouter";
import { useAuth } from "@/auth/AuthProvider";
import { SignIn } from "@/auth/SignIn";
import { Layout } from "@/components/Layout";
import { useMe } from "@/lib/queries";
import { Spinner } from "@/components/ui";
import { UserHome } from "@/pages/UserHome";
import { MyBody } from "@/pages/MyBody";
import { MyTraining } from "@/pages/MyTraining";
import { MyPeptides } from "@/pages/MyPeptides";
import { MyConversations } from "@/pages/MyConversations";
import { AdminHome } from "@/pages/AdminHome";
import { AdminParticipants } from "@/pages/AdminParticipants";
import { AdminAuditLog } from "@/pages/AdminAuditLog";

function AdminRoutes() {
  return (
    <Switch>
      <Route path="/" component={AdminHome} />
      <Route path="/admin/participants" component={AdminParticipants} />
      <Route path="/admin/audit-log" component={AdminAuditLog} />
      <Route>
        <Redirect to="/" />
      </Route>
    </Switch>
  );
}

function UserRoutes() {
  return (
    <Switch>
      <Route path="/" component={UserHome} />
      <Route path="/body" component={MyBody} />
      <Route path="/training" component={MyTraining} />
      <Route path="/peptides" component={MyPeptides} />
      <Route path="/conversations" component={MyConversations} />
      <Route>
        <Redirect to="/" />
      </Route>
    </Switch>
  );
}

function AuthedApp() {
  const me = useMe();
  if (me.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner label="Resolving your role…" />
      </div>
    );
  }
  // Authoritative role from the participant record; fall back to false on error.
  const isAdmin = me.data?.isAdmin ?? false;
  return <Layout>{isAdmin ? <AdminRoutes /> : <UserRoutes />}</Layout>;
}

export function App() {
  const { status } = useAuth();
  if (status !== "authenticated") {
    return <SignIn />;
  }
  return <AuthedApp />;
}
